import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays an animated marquee with text items and separators.
 * Supports responsive sizing, hover effects, and accessibility features.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element containing the animated content.
 * @property {HTMLElement} content - The content element with repeated items.
 *
 * @extends Component<Refs>
 */
class CustomMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  /**
   * Initialize the marquee component when connected to the DOM.
   */
  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (!content || content.firstElementChild?.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    // Event listeners for responsiveness and interactivity
    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  /**
   * Clean up event listeners when component is removed from DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * Slow down the marquee animation on pointer enter.
   */
  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  /**
   * Speed up the marquee animation on pointer leave.
   */
  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };

  /**
   * Get the cloned content element if it exists.
   * @returns {HTMLElement | null}
   */
  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Set the animation speed based on calculated value.
   * @param {number} [value] - Optional speed value in seconds.
   */
  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--custom-marquee-speed', `${value}s`);
  }

  /**
   * Calculate animation speed based on viewport width and speed factor.
   * @returns {number} Speed in seconds
   */
  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 20;
    const marqueeWidth = this.offsetWidth;
    
    if (marqueeWidth <= 0) return 30; // fallback speed
    
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return Math.max(speed, 10); // Minimum 10 seconds for smooth animation
  }

  /**
   * Handle window resize events to adjust marquee layout.
   */
  #handleResize = debounce(() => {
    const { content } = this.refs;
    if (!content) return;

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    // Adjust number of copies based on new viewport size
    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  /**
   * Restart the animation by resetting the current time.
   */
  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  /**
   * Duplicate content for seamless looping animation.
   */
  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Add repeated items to fill the marquee width.
   * @param {number} [numberOfCopies] - Number of copies to add
   */
  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (wrapper.cloneNode(true));
      content.appendChild(clone);
    }
  }

  /**
   * Remove excess repeated items.
   * @param {number} [numberOfCopies] - Number of copies to remove
   */
  #removeRepeatedItems(numberOfCopies = 1) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Calculate the number of copies needed to fill the viewport.
   * @returns {number} Number of copies needed
   */
  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement
        ? content.firstElementChild.offsetWidth
        : 1;

    if (marqueeRepeatedItemWidth <= 0 || marqueeWidth <= 0) {
      return 1;
    }

    return Math.ceil(marqueeWidth / marqueeRepeatedItemWidth) + 1;
  }
}

/**
 * Animate a numeric property smoothly using requestAnimationFrame.
 *
 * @param {Object} params - Animation parameters
 * @param {number} params.from - Starting value
 * @param {number} params.to - Ending value
 * @param {number} params.duration - Duration in milliseconds
 * @param {function(number): void} params.onUpdate - Update callback
 * @param {function(number): number} [params.easing] - Easing function (default: smoothstep)
 * @param {function(): void} [params.onComplete] - Completion callback
 * @returns {{current: number, cancel: () => void}} Animation controller
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * Animation frame callback.
   * @param {number} currentTime - Current timestamp from performance.now()
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

// Register the custom element if not already registered
if (!customElements.get('custom-marquee-component')) {
  customElements.define('custom-marquee-component', CustomMarqueeComponent);
}
